import { useEffect, useState } from 'react';
import { CheckCircle, Circle, Loader2 } from 'lucide-react';
import { api } from '../services/api';
import type { ProjectCompletion as ProjectCompletionType } from '../types';
import clsx from 'clsx';

interface ProjectCompletionProps {
  projectId: number;
  compact?: boolean;
  showDetails?: boolean;
}

const SYSTEM_ORDER = ['factsagent', 'cppages', 'images', 'serviceareapages', 'content', 'topicalmap'];

export default function ProjectCompletion({ projectId, compact = false, showDetails = true }: ProjectCompletionProps) {
  const [completion, setCompletion] = useState<ProjectCompletionType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCompletion();
  }, [projectId]);

  const loadCompletion = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/completion`);
      setCompletion(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load completion');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={clsx("flex items-center gap-2", compact ? "text-xs" : "")}>
        <Loader2 className={clsx("animate-spin", compact ? "w-3 h-3" : "w-4 h-4")} />
        <span className="text-gray-400">Loading...</span>
      </div>
    );
  }

  if (error || !completion) {
    return null;
  }

  // Compact view - just shows percentage bar
  if (compact) {
    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={clsx(
              "h-full rounded-full transition-all duration-500",
              completion.completion_percentage === 100 ? "bg-green-500" : "bg-brand-500"
            )}
            style={{ width: `${completion.completion_percentage}%` }}
          />
        </div>
        <span className={clsx(
          "text-xs font-medium",
          completion.completion_percentage === 100 ? "text-green-600" : "text-gray-500"
        )}>
          {completion.completion_percentage}%
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Project Completion</h3>
          <p className="text-sm text-gray-500">
            {completion.completed_systems.length} of 6 systems completed
          </p>
        </div>
        <div className={clsx(
          "text-3xl font-bold",
          completion.completion_percentage === 100 ? "text-green-600" : "text-brand-600"
        )}>
          {completion.completion_percentage}%
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={clsx(
            "h-full rounded-full transition-all duration-500",
            completion.completion_percentage === 100 ? "bg-green-500" : "bg-brand-500"
          )}
          style={{ width: `${completion.completion_percentage}%` }}
        />
      </div>

      {/* System Breakdown */}
      {showDetails && (
        <div className="grid grid-cols-6 gap-2 mt-4">
          {SYSTEM_ORDER.map((systemKey) => {
            const system = completion.system_status[systemKey];
            if (!system) return null;

            return (
              <div
                key={systemKey}
                className={clsx(
                  "p-3 rounded-lg text-center transition-all",
                  system.completed
                    ? "bg-green-50 border border-green-200"
                    : "bg-gray-50 border border-gray-200"
                )}
              >
                <div className="flex justify-center mb-2">
                  {system.completed ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <p className={clsx(
                  "text-xs font-medium",
                  system.completed ? "text-green-700" : "text-gray-600"
                )}>
                  {system.name}
                </p>
                {system.outputs_count > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {system.outputs_count} outputs
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Small inline version for lists
export function CompletionBadge({ projectId }: { projectId: number }) {
  const [completion, setCompletion] = useState<ProjectCompletionType | null>(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/completion`)
      .then(res => setCompletion(res.data))
      .catch(() => {});
  }, [projectId]);

  if (!completion) return null;

  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={clsx(
            "h-full rounded-full",
            completion.completion_percentage === 100 ? "bg-green-500" : "bg-brand-500"
          )}
          style={{ width: `${completion.completion_percentage}%` }}
        />
      </div>
      <span className={clsx(
        "text-xs font-medium",
        completion.completion_percentage === 100 ? "text-green-600" : "text-gray-500"
      )}>
        {completion.completion_percentage}%
      </span>
    </div>
  );
}
